import { buildExpansionComparison } from "../expansion/expansionReportMetrics";
import { etapasVisiblesPipeline } from "../pipelineUtils";

const DAY_MS = 86400000;

export function toDateKey(value) {
  if (!value) return null;
  // Calendar dates and legacy local timestamps already use the display day.
  // MCP timestamps carry UTC/offset information and must be converted locally.
  if (typeof value === "string" && /^\d{4}-\d{2}-\d{2}/.test(value)
    && !/(?:Z|[+-]\d{2}:?\d{2})$/i.test(value)) return value.slice(0, 10);
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return null;
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const day = String(date.getDate()).padStart(2, "0");
  return `${year}-${month}-${day}`;
}

function dateFromKey(key) {
  const [year, month, day] = key.split("-").map(Number);
  return new Date(year, month - 1, day);
}

function addDays(key, amount) {
  const date = dateFromKey(key);
  date.setDate(date.getDate() + amount);
  return toDateKey(date);
}

export function validateReportRange(from, to) {
  if (!toDateKey(from) || !toDateKey(to)) return "Selecciona ambas fechas.";
  if (from > to) return "La fecha inicial no puede ser posterior a la final.";
  return "";
}

export function getPreviousPeriod(from, to) {
  if (validateReportRange(from, to)) return null;
  const days = Math.round((dateFromKey(to) - dateFromKey(from)) / DAY_MS) + 1;
  const previousTo = addDays(from, -1);
  return { from: addDays(previousTo, -(days - 1)), to: previousTo, days };
}

export function isInRange(value, from, to) {
  const key = toDateKey(value);
  return Boolean(key && key >= from && key <= to);
}

export function percentChange(current, previous) {
  if (current == null || previous == null) return null;
  if (!previous) return current ? null : 0;
  return Math.round(((current - previous) / Math.abs(previous)) * 1000) / 10;
}

const leadDate = (lead) => lead.creadoEn || lead.fechaContacto;
const money = (value) => Number(value) || 0;

function aggregate(items, keyFor, valueFor = () => 0) {
  const map = new Map();
  items.forEach((item) => {
    const key = keyFor(item) || "Sin asignar";
    const current = map.get(key) || { nombre: key, cantidad: 0, total: 0 };
    current.cantidad += 1;
    current.total += money(valueFor(item));
    map.set(key, current);
  });
  return [...map.values()].sort((a, b) => b.total - a.total || b.cantidad - a.cantidad);
}

function productRows(ventas) {
  const rows = [];
  ventas.forEach((venta) => {
    if (venta.items?.length) {
      venta.items.forEach((item) => {
        rows.push({
          nombre: item.productoNombre || "Sin producto",
          cantidad: Number(item.cantidad) || 1,
          total: money(item.precioUnitario) * (Number(item.cantidad) || 1),
        });
      });
      return;
    }
    rows.push({
      nombre: venta.productoNombre || "Sin producto",
      cantidad: 1,
      total: money(venta.total),
    });
  });
  const map = new Map();
  rows.forEach((row) => {
    const current = map.get(row.nombre) || { nombre: row.nombre, cantidad: 0, total: 0 };
    current.cantidad += row.cantidad;
    current.total += row.total;
    map.set(row.nombre, current);
  });
  return [...map.values()].sort((a, b) => b.total - a.total || b.cantidad - a.cantidad);
}

export function buildStageMetrics(historial, from, to, config) {
  const terminalLoss = new Set(["perdido", "perdida", "pospuesta"]);
  const stages = config?.etapasCatalogo?.length ? config.etapasCatalogo : (config?.etapasPipeline || []).map(id => ({ id, nombre: id }));
  const order = new Map(stages.filter(s => !terminalLoss.has(s.id)).map((s, i) => [s.id, i]));
  const stats = new Map(stages.map(s => [s.id, { entrants: new Set(), progressed: new Set(), salidas: 0, durations: [] }]));
  const entered = new Map();
  for (const event of [...historial].sort((a, b) => String(a.fechaHora).localeCompare(String(b.fechaHora)))) {
    if (toDateKey(event.fechaHora) > to) continue;
    const key = event.leadId + ":" + event.etapaAnterior;
    const previous = entered.get(key);
    const source = stats.get(event.etapaAnterior);
    const inRange = isInRange(event.fechaHora, from, to);
    if (inRange && source) {
      source.salidas++;
      if (previous) {
        const days = (new Date(event.fechaHora) - new Date(previous.fechaHora)) / DAY_MS;
        if (Number.isFinite(days) && days >= 0) source.durations.push(days);
      }
    }
    if (!terminalLoss.has(event.etapaNueva)) {
      for (const stage of stages) if ((order.get(event.etapaNueva) ?? -1) > (order.get(stage.id) ?? Infinity) && stats.get(stage.id).entrants.has(event.leadId)) stats.get(stage.id).progressed.add(event.leadId);
    }
    if (event.etapaAnterior) entered.delete(key);
    const target = stats.get(event.etapaNueva);
    if (target) {
      entered.set(event.leadId + ":" + event.etapaNueva, event);
      if (inRange) target.entrants.add(event.leadId);
    }
  }
  // Keep hidden stages and their events in the calculations so hiding a step
  // cannot change progression or time spent in the remaining steps.
  const visibleIds = new Set(etapasVisiblesPipeline(config).map(stage => stage.id));
  return stages.filter(stage => visibleIds.has(stage.id)).map(stage => {
    const row = stats.get(stage.id);
    return { id: stage.id, nombre: stage.nombre || stage.id, entradas: row.entrants.size, salidas: row.salidas, conversion: row.entrants.size ? Math.round(row.progressed.size / row.entrants.size * 1000) / 10 : 0, diasPromedio: row.durations.length ? Math.round(row.durations.reduce((s, n) => s + n, 0) / row.durations.length * 10) / 10 : null };
  });
}

function buildDailySeries(period, leads, ventas, contactos, conversiones) {
  const group = (rows, dateFor) => { const map = new Map(); for (const row of rows) { const key = toDateKey(dateFor(row)); if (!map.has(key)) map.set(key, []); map.get(key).push(row); } return map; };
  const salesByDay = group(ventas, r => r.fecha), leadsByDay = group(leads, leadDate), contactsByDay = group(contactos, r => r.fecha), conversionsByDay = group(conversiones, r => r.fechaHora);
  return Array.from({ length: period.days }, (_, index) => {
    const date = addDays(period.from, index);
    const daySales = salesByDay.get(date) || [];
    const dayLeads = leadsByDay.get(date) || [];
    return {
      index: index + 1,
      fecha: date,
      etiqueta: date.slice(5),
      leads: dayLeads.length,
      contactos: (contactsByDay.get(date) || []).length,
      ventas: daySales.length,
      ingresos: daySales.reduce((sum, item) => sum + money(item.total), 0),
      conversiones: (conversionsByDay.get(date) || []).length,
    };
  });
}

function periodData(store, period) {
  const allLeads = store.leads || [];
  const allSales = (store.ventasActivas || store.ventas || []).filter((sale) => !sale.anulada);
  const allContacts = store.contactosHistorial || [];
  const allHistory = store.historialEtapas || [];
  const leads = allLeads.filter((lead) => isInRange(leadDate(lead), period.from, period.to));
  const ventas = allSales.filter((sale) => isInRange(sale.fecha, period.from, period.to));
  const contactos = allContacts.filter((contact) => isInRange(contact.fecha, period.from, period.to));
  const historial = allHistory.filter((entry) => isInRange(entry.fechaHora, period.from, period.to));
  const conversiones = historial.filter((entry) => entry.etapaNueva === "inscrito");
  const ingresos = ventas.reduce((sum, sale) => sum + money(sale.total), 0);
  const cohortLeadIds = new Set(leads.map((lead) => lead.id));
  const convertedLeadIds = new Set();
  const leadsById = new Map(allLeads.map(l => [l.id, l]));
  const firstSales = new Map();
  const bySale = new Map(allSales.map(s => [s.id, s]));
  for (const sale of (store.customerSalesHistory || allSales)) if (!sale.anulada && sale.leadId && (!firstSales.has(sale.leadId) || sale.fecha < firstSales.get(sale.leadId).fecha)) firstSales.set(sale.leadId, sale);
  convertedLeadIds.clear();
  for (const [leadId, sale] of firstSales) if (cohortLeadIds.has(leadId) && toDateKey(sale.fecha) <= period.to) convertedLeadIds.add(leadId);
  const newBuyers = [...firstSales.values()].filter(s => bySale.has(s.id) && isInRange(s.fecha, period.from, period.to));
  const payments = (store.pagosCredito || []).filter(p => bySale.get(p.ventaId)?.esCredito);
  const cobrado = ventas.filter(s => !s.esCredito).reduce((sum, s) => sum + money(s.total), 0) + payments.filter(p => isInRange(p.fecha, period.from, period.to)).reduce((sum, p) => sum + money(p.monto), 0);
  const paidAtEnd = new Map();
  for (const p of payments) if (toDateKey(p.fecha) <= period.to) paidAtEnd.set(p.ventaId, (paidAtEnd.get(p.ventaId) || 0) + money(p.monto));
  const pendiente = allSales.filter(s => s.esCredito && toDateKey(s.fecha) <= period.to).reduce((sum, s) => sum + Math.max(0, money(s.total) - (paidAtEnd.get(s.id) || 0)), 0);
  const opportunities = store.oportunidadesExpansion || [];
  const firstOpportunitySales = new Map();
  for (const sale of (store.customerSalesHistory || allSales)) if (!sale.anulada && sale.opportunityId && (!firstOpportunitySales.has(sale.opportunityId) || sale.fecha < firstOpportunitySales.get(sale.opportunityId).fecha)) firstOpportunitySales.set(sale.opportunityId, sale);
  const ganados = [...firstOpportunitySales.values()].filter(s => bySale.has(s.id) && isInRange(s.fecha, period.from, period.to)).length + newBuyers.filter(s => !s.opportunityId).length;
  const perdidos = opportunities.filter(o => (o.historialEtapas || []).some(e => e.etapaNueva === "perdida" && isInRange(e.fechaHora, period.from, period.to))).length + new Set(historial.filter(h => h.etapaNueva === "perdido" && !opportunities.some(o => o.leadId === h.leadId)).map(h => h.leadId)).size;
  const cycle = newBuyers.map(s => { const lead = leadsById.get(s.leadId); return lead && toDateKey(leadDate(lead)) ? (dateFromKey(toDateKey(s.fecha)) - dateFromKey(toDateKey(leadDate(lead)))) / DAY_MS : null; }).filter(n => n !== null && Number.isFinite(n) && n >= 0);
  const vendedores = aggregate(ventas, (sale) => sale.vendedorNombre || "Sin asignar", (sale) => sale.total);
  const canales = aggregate(
    ventas,
    (sale) => leadsById.get(sale.leadId)?.canal || "directo",
    (sale) => sale.total
  );

  return {
    period,
    leads,
    ventas,
    contactos,
    historial,
    kpis: {
      leads: leads.length,
      ventas: ventas.length,
      ingresos,
      cobrado,
      pendiente,
      ganados,
      perdidos,
      diasCierre: cycle.length ? Math.round(cycle.reduce((s, n) => s + n, 0) / cycle.length * 10) / 10 : null,
      compradoresRecurrentes: new Set(ventas.filter(s => s.leadId && !newBuyers.some(first => first.leadId === s.leadId)).map(s => s.leadId)).size,
      ticketPromedio: ventas.length ? Math.round((ingresos / ventas.length) * 100) / 100 : 0,
      conversiones: new Set(conversiones.map((entry) => entry.leadId)).size,
      tasaConversion: leads.length ? Math.round((convertedLeadIds.size / leads.length) * 1000) / 10 : 0,
      contactos: contactos.length,
      clientesNuevos: newBuyers.length,
    },
    canales,
    vendedores,
    productos: productRows(ventas),
    etapas: buildStageMetrics(allHistory, period.from, period.to, store.config),
    daily: buildDailySeries(period, leads, ventas, contactos, conversiones),
  };
}

function pairRankings(current, previous, key = "nombre") {
  const names = new Set([...current.map((item) => item[key]), ...previous.map((item) => item[key])]);
  return [...names].map((name) => {
    const a = current.find((item) => item[key] === name) || { cantidad: 0, total: 0 };
    const b = previous.find((item) => item[key] === name) || { cantidad: 0, total: 0 };
    return {
      nombre: name,
      cantidadActual: a.cantidad,
      cantidadAnterior: b.cantidad,
      totalActual: a.total,
      totalAnterior: b.total,
      variacion: percentChange(a.total, b.total),
    };
  }).sort((a, b) => b.totalActual - a.totalActual);
}

export function buildCRMComparison(store, from, to, previousRange = null) {
  const error = validateReportRange(from, to);
  if (error) throw new Error(error);
  const previous = previousRange ? { ...previousRange, days: Math.round((dateFromKey(previousRange.to) - dateFromKey(previousRange.from)) / DAY_MS) + 1 } : getPreviousPeriod(from, to);
  const currentPeriod = { from, to, days: Math.round((dateFromKey(to) - dateFromKey(from)) / DAY_MS) + 1 };
  const current = periodData(store, currentPeriod);
  const prior = periodData(store, previous);
  const kpiKeys = Object.keys(current.kpis);
  const kpis = Object.fromEntries(kpiKeys.map((key) => [
    key,
    {
      actual: current.kpis[key],
      anterior: prior.kpis[key],
      variacion: percentChange(current.kpis[key], prior.kpis[key]),
    },
  ]));

  const daily = current.daily.map((day, index) => ({
    index: day.index,
    fechaActual: day.fecha,
    fechaAnterior: prior.daily[index]?.fecha,
    ingresosActual: day.ingresos,
    ingresosAnterior: prior.daily[index]?.ingresos || 0,
    ventasActual: day.ventas,
    ventasAnterior: prior.daily[index]?.ventas || 0,
    leadsActual: day.leads,
    leadsAnterior: prior.daily[index]?.leads || 0,
    contactosActual: day.contactos,
    contactosAnterior: prior.daily[index]?.contactos || 0,
    conversionesActual: day.conversiones,
    conversionesAnterior: prior.daily[index]?.conversiones || 0,
  }));

  return {
    generatedAt: new Date().toISOString(),
    current,
    previous: prior,
    kpis,
    daily,
    rankings: {
      canales: pairRankings(current.canales, prior.canales),
      vendedores: pairRankings(current.vendedores, prior.vendedores),
      productos: pairRankings(current.productos, prior.productos),
    },
    expansion: buildExpansionComparison(store, from, to, previous),
  };
}

export function buildOperationalDatasets(comparison) {
  const { current } = comparison;
  return {
    leads: current.leads.map((lead) => ({
      fecha: toDateKey(leadDate(lead)),
      nombre: lead.nombre || "",
      telefono: lead.telefono || "",
      productoServicio: lead.cursoInteres || "",
      montoEstimado: lead.montoEstimado ?? "",
      canal: lead.canal || "",
      etapa: lead.etapa || "",
      zona: lead.zona || "",
    })),
    ventas: current.ventas.map((sale) => ({
      fecha: toDateKey(sale.fecha),
      cliente: sale.nombreCliente || "",
      productoServicio: sale.productoNombre || (sale.items || []).map((item) => item.productoNombre).join(", "),
      vendedor: sale.vendedorNombre || "",
      total: money(sale.total),
      efectivo: money(sale.montoEfectivo),
      transferencia: money(sale.montoTransferencia),
      credito: sale.esCredito ? "Sí" : "No",
    })),
    contactos: current.contactos.map((contact) => ({
      fecha: contact.fecha || "",
      leadId: contact.leadId || "",
      tipo: contact.tipo || "",
      nota: contact.nota || "",
    })),
    movimientosEtapa: current.historial.map((entry) => ({
      fecha: entry.fechaHora || "",
      leadId: entry.leadId || "",
      etapaAnterior: entry.etapaAnterior || "",
      etapaNueva: entry.etapaNueva || "",
    })),
  };
}

export function getMonthlyComparison(month, today = toDateKey(new Date())) {
  const [year, number] = month.split("-").map(Number);
  const last = new Date(year, number, 0).getDate();
  const currentMonth = today.slice(0, 7) === month;
  const cutoff = currentMonth ? Number(today.slice(8, 10)) : last;
  const priorStart = new Date(year, number - 2, 1);
  const priorLast = new Date(year, number - 1, 0).getDate();
  return { from: month + "-01", to: month + "-" + String(cutoff).padStart(2, "0"), previous: { from: toDateKey(priorStart), to: toDateKey(new Date(priorStart.getFullYear(), priorStart.getMonth(), currentMonth ? Math.min(cutoff, priorLast) : priorLast)) } };
}

export function buildMonthlySalesSeries(store, year) {
  const sales = (store.ventasActivas || store.ventas || []).filter(v => !v.anulada);
  const creditIds = new Set(sales.filter(v => v.esCredito).map(v => v.id));
  const first = new Map();
  for (const sale of (store.customerSalesHistory || sales)) if (!sale.anulada && sale.leadId && (!first.has(sale.leadId) || sale.fecha < first.get(sale.leadId).fecha)) first.set(sale.leadId, sale);
  const rows = Array.from({ length: 12 }, (_, i) => ({ mes: year + "-" + String(i + 1).padStart(2, "0"), vendido: 0, cobrado: 0, clientesNuevos: 0 }));
  const byMonth = new Map(rows.map(r => [r.mes, r]));
  for (const sale of sales) { const row = byMonth.get(sale.fecha?.slice(0, 7)); if (row) { row.vendido += money(sale.total); if (!sale.esCredito) row.cobrado += money(sale.total); } }
  for (const payment of store.pagosCredito || []) { const row = byMonth.get(payment.fecha?.slice(0, 7)); if (row && creditIds.has(payment.ventaId)) row.cobrado += money(payment.monto); }
  const saleIds = new Set(sales.map(s => s.id));
  for (const sale of first.values()) { const row = byMonth.get(sale.fecha?.slice(0, 7)); if (row && saleIds.has(sale.id)) row.clientesNuevos++; }
  return rows;
}
